import api from '@shared/services/api';
import { t } from 'i18next';

export const userService = {

    getUsers: async ({ searchQuery, pageNumber = 0, pageSize = 10, sortBy = 'username', sortDir = 'asc' }) => {
        try {
            const params = {
                page: pageNumber,
                size: pageSize,
                sortBy: sortBy || 'username',
                sortOrder: sortDir,
                ...(searchQuery?.username && { username: searchQuery.username }),
                ...(searchQuery?.accountname && { accountname: searchQuery.accountname })
            };

            const response = await api.get('/users', { params });
            const { _embedded, page } = response.data;
            if (!_embedded || !_embedded.usersResponseDTOList){
                console.error('Invalid response - no content:', response);
                throw new Error(t('common.dataNotFound'));
            }

            return {
                content: _embedded.usersResponseDTOList,                
                totalPages: page.totalPages,
                totalElements: page.totalElements,
                pageNumber: page.pageable?.pageNumber || pageNumber
            };
        } catch (error) {
            console.error('Error fetching/searching users:', error);
            throw error;
        }
    },

    getUserById: async (userId) => {
        try {
            const response = await api.get(`/users/${userId}`);
            const user = response.data;
            const groupIds = Array.isArray(user.groups)
                ? user.groups.map((group) => (typeof group === 'object' && group !== null ? group.id : group))
                : [];

            return {
                id: user.id,
                username: user.username || '',
                providerId: user.providerId,
                specrights: user.specrights,
                empId: user.empId ?? '',
                employeeNumber: user.empId ?? '',
                ldapAuth: user.ldapAuth ?? false,
                ldapAccess: user.ldapAuth ?? false,
                locked: user.locked ?? false,
                isLocked: user.locked ?? false,
                pwdLastChange: user.pwdLastChange,
                personId: user.personId,
                groups: groupIds,
                enabled: user.enabled ?? true,
                accountNonExpired: user.accountNonExpired ?? true,
                credentialsNonExpired: user.credentialsNonExpired ?? true,
                accountNonLocked: user.accountNonLocked ?? true,
                deleted: user.deleted ?? false,
                accountExpiryDate: user.accountExpiryDate,
                description: user.description ?? '',
                extendedAccess: user.extendedAccess ?? false,
            };
        } catch (error) {
            console.error('Error fetching user:', error);
            throw error;
        }
    },

    updateUser: async (userId, userData) => {
        try {
            const response = await api.put(`/users/${userId}`, {
                language: "test",
                providerId: 0,
                username: userData.username,
                groups: userData.groups,
                fullname: "test",
                description: userData.description || null,
                address: "string",
                phone: "string4",
                password: userData.password,
                specRights: "string",
                onlyWebAccess: true,
                empId: userData.empId || userData.employeeNumber || "string",
                ldapAuth: userData.ldapAccess !== undefined ? userData.ldapAccess : (userData.ldapAuth !== undefined ? userData.ldapAuth : true),
                isLocked: userData.isLocked !== undefined ? userData.isLocked : (userData.locked !== undefined ? userData.locked : false),
                extendedAccess: userData.extendedAccess !== undefined ? userData.extendedAccess : false,
                pwdLastChange: "2024-11-12",
                personId: userData.personId ? parseInt(userData.personId) : 1
            });

            return response.data;
        } catch (error) {
            console.error('Error updating user:', error);
            throw error;
        }
    },

    deleteUser: async (userId) => {
        try {
            const response = await api.delete(`/users/${userId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting user:', error);
            throw error;
        }
    },

    createUser: async (userData) => {
        try {
            const userDataWithPersonId = {
                ...userData,
                description: userData.description || null,
            };

            const response = await api.post('/users', userDataWithPersonId);
            if (!response.data) {
                console.error('Invalid response - no data:', response);
                throw new Error(t('common.dataNotFound'));
            }
            return response.data;
        } catch (error) {
            console.error('Error creating user:', error);
            throw error;
        }
    },

    updateUserGroups: async (userId, groups) => {
        try {
            const currentUser = await userService.getUserById(userId);
            const formattedGroups = groups.map((groupId) => parseInt(groupId, 10));
            
            const response = await api.put(`/users/${userId}`, {
                language: currentUser.language || "test",
                providerId: 0,
                username: currentUser.username,
                groups: formattedGroups,  
                fullname: currentUser.fullname || "test",
                description: currentUser.description || "string",
                address: "string",
                phone: currentUser.phone || "string4",
                password: "string",
                specRights: "string",
                onlyWebAccess: currentUser.onlyWebAccess || true,
                empId: currentUser.empId || "string",
                ldapAuth: currentUser.ldapAuth || true,
                isLocked: currentUser.isLocked || false,
                pwdLastChange: "2024-11-12",
                personId: 1
            });
            return response.data;
        } catch (error) {
            console.error('Error updating user groups:', error);
            throw error;
        }
    },
}